import React, { useMemo } from 'react';
import ShiftCard from './ShiftCard';
import type { Shift } from '../../dtos/scheduling/schedule.dto';
import { clinicianWeekRows, dayLabel, formatMinutes } from './scheduleUtils';

export interface ClinicianWeekProps {
  shifts: Shift[];
  days: string[];
  today: string;
  selectedIds: Set<string>;
  onToggleSelect: (shift: Shift) => void;
  onOpen: (shift: Shift) => void;
}

/** Rows are clinicians (Unstaffed first), with their scheduled hours for the week. */
const ClinicianWeek: React.FC<ClinicianWeekProps> = ({ shifts, days, today, selectedIds, onToggleSelect, onOpen }) => {
  const rows = useMemo(() => clinicianWeekRows(shifts, days), [shifts, days]);
  if (!rows.length) return <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">No shifts this week.</div>;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="w-full min-w-[960px] table-fixed border-collapse text-left">
        <thead className="sticky top-0 z-10 bg-gray-50">
          <tr>
            <th scope="col" className="w-48 border-b border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600">Clinician · hours</th>
            {days.map((d) => <th key={d} scope="col" className={`border-b border-l border-gray-200 px-2 py-2 text-xs font-semibold ${d === today ? 'text-sky-700' : 'text-gray-600'}`}>{dayLabel(d)}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.providerId || 'unstaffed'} className={row.providerId ? '' : 'bg-red-50/40'}>
              <th scope="row" className="border-b border-gray-200 px-3 py-2 align-top font-normal">
                <div className={`truncate text-sm font-semibold ${row.providerId ? 'text-gray-900' : 'text-red-700'}`}>{row.name}</div>
                <div className="text-xs text-gray-500">{row.discipline} · {formatMinutes(row.minutes)}</div>
              </th>
              {days.map((d) => (
                <td key={d} className={`border-b border-l border-gray-200 p-1 align-top ${d === today ? 'bg-sky-50/50' : ''}`}>
                  <div className="space-y-1">
                    {row.days[d].map((s) => (
                      <ShiftCard key={s.id} shift={s} variant="compact" selected={selectedIds.has(s.id)} onSelect={onToggleSelect} onOpen={onOpen} />
                    ))}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ClinicianWeek;
